import { CommerceConf } from '@gooduncles/gu-app-schema';
import { add, addDays, getHours } from 'date-fns';
import { useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { deleteTimestamp } from 'src/utils/common';
import { formatDate, getKoreaDate } from 'src/utils/date-util';

import { setCommerceConf } from '../slices/commerceConf';
import { RootState } from '../store';

export const useCommerceConf = () => {
  const dispatch = useDispatch();
  const { commerceConf } = useSelector((state: RootState) => state.commerceConf);
  const onSetCommerceConf = useCallback(
    (commerceConf: CommerceConf | null) => {
      dispatch(setCommerceConf({ commerceConf: deleteTimestamp(commerceConf) }));
    },
    [dispatch]
  );

  const getDeliveryDate = (date?: Date) => {
    if (!commerceConf?.deliveryDateRules) {
      return null;
    }
    const targetDate = date ?? new Date();
    const weekday = targetDate.getDay();
    const days = commerceConf.deliveryDateRules[weekday];
    return addDays(targetDate, days);
  };

  /**
   * 현재 시간에 따라 대시보드의 기준 날짜를 정한다.(대게 10시 ~ 24시 사이인 경우 내일{배송일}, 그 외는 오늘의 날짜를 반환한다.)
   */
  const getDeliveryDateForNow = useCallback(() => {
    if (!commerceConf?.deliveryDateRules) {
      return null;
    }
    const now = getKoreaDate();
    const currentHour = getHours(now);
    const deliveryDateRules = commerceConf.deliveryDateRules;
    const minHour = 10;
    // 현재 시간이 10시 보다 작으면 오늘 날짜를, 10시 이상이면 배송일을 반환한다.
    if (currentHour < minHour) {
      return formatDate(now, 'yyyy-MM-dd');
    }
    const weekday = now.getDay();
    const deliveryDate = add(now, { days: deliveryDateRules[weekday] });
    return formatDate(deliveryDate, 'yyyy-MM-dd');
  }, [commerceConf?.deliveryDateRules]);

  /**
   * 주문일로부터 배송 날짜를 계산한다.
   */
  const getDeliveryDateForOrder = useCallback(
    (orderDate: string) => {
      if (!commerceConf?.deliveryDateRules) {
        return null;
      }
      const deliveryDateRules = commerceConf.deliveryDateRules;
      const orderDay = new Date(orderDate).getDay();
      const deliveryDay = deliveryDateRules[orderDay];
      return formatDate(add(new Date(orderDate), { days: deliveryDay }), 'yyyy-MM-dd');
    },
    [commerceConf?.deliveryDateRules]
  );

  return {
    commerceConf,
    onSetCommerceConf,
    getDeliveryDate,
    getDeliveryDateForOrder,
    getDeliveryDateForNow,
  };
};
